import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Paginator } from "primereact/paginator";
import Loading from "../../../Components/Loading";
import CustomDataTable from "../../../Components/CustomDataTable";
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import { useDateFormatter } from "../../../Components/FormatDateTime";
import { SegmentCreate } from "./segmentCreate";
import { EditSegmentDialog } from "./EditSegmentDialog";
import {
    getSegmentDisplayName,
    getSegmentId,
    getSegmentsTotalElements,
    normalizeSegmentsList,
} from "./segmentUtils";

const SEGMENTS_PAGE_SIZE = 15;

const SegmentsTab = ({ businessId }) => {
    const { t } = useTranslation("common");
    const { formatDateTime } = useDateFormatter();
    const dispatch = useDispatch();

    const {
        searchingSegments,
        segmentSearchResults,
        fetchingSegment,
        segmentSuccess,
        segmentListTotalElements,
        segmentError,
    } = useSelector((state) => state.businessCustomers);

    const searchSegments = useCallback(
        (id, name) => dispatch(BusinessCustomersActions.segmentSearchRequest(id, name)),
        [dispatch]
    );

    const fetchSegmentsPage = useCallback(
        (page, size = SEGMENTS_PAGE_SIZE) => {
            if (!businessId) {
                return;
            }
            dispatch(
                BusinessCustomersActions.fetchingSegmentRequest(businessId, {
                    page,
                    size,
                    sort: "createdDate,desc",
                })
            );
        },
        [businessId, dispatch]
    );

    const [createVisible, setCreateVisible] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [segmentToEdit, setSegmentToEdit] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [pageState, setPageState] = useState({
        page: 0,
        first: 0,
        size: SEGMENTS_PAGE_SIZE,
    });

    const isSearchMode = appliedSearch.length > 0;

    const segmentPageOptions = useMemo(
        () => ({
            page: pageState.page,
            size: pageState.size,
            sort: "createdDate,desc",
        }),
        [pageState.page, pageState.size]
    );

    const loadSegments = useCallback(() => {
        fetchSegmentsPage(pageState.page, pageState.size);
    }, [fetchSegmentsPage, pageState.page, pageState.size]);

    const refreshSegments = useCallback(() => {
        loadSegments();
        if (appliedSearch && businessId) {
            searchSegments(businessId, appliedSearch);
        }
    }, [loadSegments, appliedSearch, businessId, searchSegments]);

    const runSegmentSearch = useCallback(() => {
        const query = searchText.trim();
        setAppliedSearch(query);
        if (query && businessId) {
            searchSegments(businessId, query);
        }
    }, [searchText, businessId, searchSegments]);

    const clearSegmentSearch = useCallback(() => {
        setSearchText("");
        setAppliedSearch("");
        loadSegments();
    }, [loadSegments]);

    useEffect(() => {
        if (businessId && !isSearchMode) {
            fetchSegmentsPage(pageState.page, pageState.size);
        }
    }, [businessId, isSearchMode, fetchSegmentsPage, pageState.page, pageState.size]);

    const onPageChange = (e) => {
        const isRowsChanged = pageState.size !== e.rows;
        if (isRowsChanged) {
            setPageState({ page: 0, first: 0, size: e.rows });
        } else {
            setPageState({ page: e.page, first: e.first, size: e.rows });
        }
    };

    const segments = useMemo(() => {
        const raw = isSearchMode ? segmentSearchResults : segmentSuccess;
        return normalizeSegmentsList(raw).filter(
            (segment) => getSegmentId(segment) != null && getSegmentDisplayName(segment)
        );
    }, [segmentSuccess, segmentSearchResults, isSearchMode]);

    const totalRecords = isSearchMode
        ? getSegmentsTotalElements(segmentSearchResults)
        : segmentListTotalElements ?? getSegmentsTotalElements(segmentSuccess);

    const tableLoading = isSearchMode ? searchingSegments : fetchingSegment;

    const openEdit = (segment) => {
        setSegmentToEdit(segment);
        setEditVisible(true);
    };

    const nameBody = (rowData) => (
        <div className="segments-name-cell">
            <div className="segment-tag-div">
                <span className="segment-class">{getSegmentDisplayName(rowData)}</span>
            </div>
            {rowData.createdDate ? (
                <>
                    <span className="createdOnLabel">
                        {t("segment.created_on")}
                        {":"}
                    </span>
                    <span className="createdOn">{formatDateTime(rowData.createdDate)}</span>
                </>
            ) : null}
        </div>
    );

    const lastModifiedByBody = (rowData) => {
        if (!rowData.lastModifiedDate && !rowData.lastModifiedBy) {
            return "—";
        }
        return (
            <div className="segments-modified-cell">
                {rowData.lastModifiedDate ? (
                    <>
                        <span className="createdOnLabel">
                            {t("segment.modified_on")}
                            {":"}
                        </span>
                        <span className="createdOn">{formatDateTime(rowData.lastModifiedDate)}</span>
                    </>
                ) : null}
                {rowData.lastModifiedBy ? (
                    <span className="createdOn">{rowData.lastModifiedBy}</span>
                ) : null}
            </div>
        );
    };

    const actionsBody = (rowData) => (
        <div className="products-action-cell segments-action-cell-left">
            <div className="car-buttons table_action_button">
                <Button
                    type="button"
                    icon="pi pi-pencil"
                    className="p-button active-action-button"
                    id="action-button"
                    tooltip={t("edit")}
                    tooltipOptions={{
                        position: "bottom",
                        mouseTrack: true,
                        mouseTrackTop: 15,
                    }}
                    onClick={() => openEdit(rowData)}
                />
            </div>
        </div>
    );

    const columns = [
        {
            field: "name",
            header: t("common.name"),
            body: nameBody,
            className: "segments-column-left",
            bodyClassName: "segments-column-left",
            alignHeader: "left",
            align: "left",
        },
        {
            field: "lastModifiedBy",
            header: t("segment.last_modified_by"),
            body: lastModifiedByBody,
            className: "segments-column-left",
            bodyClassName: "segments-column-left",
            alignHeader: "left",
            align: "left",
        },
        {
            field: "actions",
            header: t("common.actions"),
            body: actionsBody,
            className: "products-action-column segments-column-left",
            bodyClassName: "segments-column-left",
            alignHeader: "left",
            align: "left",
        },
    ];

    const tableHeader = (
        <div className="segments-table-toolbar">
            <div className="segments-search">
                <div className="subscribers-search-input">
                    <InputText
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={t("segment.search_placeholder")}
                        className="w-full"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                runSegmentSearch();
                            }
                        }}
                    />
                    <Button
                        type="button"
                        onClick={runSegmentSearch}
                        icon="pi pi-search"
                        className="p-button-info p-button-text subscribers-search-btn"
                        aria-label={t("segment.search_placeholder")}
                    />
                    {searchText ? (
                        <Button
                            type="button"
                            onClick={clearSegmentSearch}
                            icon="pi pi-times"
                            className="p-button-danger p-button-text subscribers-search-clear-btn"
                            aria-label={t("common.close")}
                        />
                    ) : null}
                </div>
            </div>
            <Button
                className="segment_button import_button"
                icon="pi pi-plus-circle"
                label={t("business_customers.create_segment")}
                onClick={() => setCreateVisible(true)}
            />
        </div>
    );

    if (fetchingSegment && segmentSuccess == null && !segmentError && !isSearchMode) {
        return <Loading />;
    }

    return (
        <>
            <div className="customers-screen">
                <CustomDataTable
                    value={segments}
                    paginator={false}
                    responsiveLayout="stack"
                    columns={columns}
                    dataKey="id"
                    header={tableHeader}
                    tableClassName="segments-data-table"
                    rowClassName={() => "subscribers-data-table segments-list-row"}
                    loading={tableLoading}
                    emptyMessage={t("segment.empty_state")}
                />
                {!isSearchMode ? (
                    <Paginator
                        rows={pageState.size}
                        totalRecords={totalRecords ?? 0}
                        first={pageState.first}
                        onPageChange={onPageChange}
                        className="custom-paginator"
                    />
                ) : null}
            </div>

            <SegmentCreate
                visible={createVisible}
                hidePopup={() => setCreateVisible(false)}
                businessId={businessId}
                onSegmentsChanged={refreshSegments}
                segmentFetchOptions={segmentPageOptions}
            />

            <EditSegmentDialog
                visible={editVisible}
                onHide={() => {
                    setEditVisible(false);
                    setSegmentToEdit(null);
                }}
                businessId={businessId}
                segment={segmentToEdit}
                onSegmentsChanged={refreshSegments}
                segmentFetchOptions={segmentPageOptions}
            />
        </>
    );
};

export default SegmentsTab;
